// Keeps a figure <input> to what a figure can hold, whatever is typed or pasted. The app has no
// type="number" fields: they have a spinner nobody uses, and the scroll wheel changes the value
// under the pointer. Figure fields are type="text" with an inputmode, and this is what keeps
// letters out. Only characters that can't be part of a figure are dropped (letters, spaces, a
// pasted "AED" or thousands comma). A minus or a decimal point the field doesn't take - a minus
// without { negative: true }, a point with { integer: true } - stays where it was typed and the
// field is marked invalid, because dropping it would turn one amount into another: 1.5 into 15,
// -50 into 50. A figure outside the field's min/max attributes is marked invalid the same way.
// Whatever saves the field checks :invalid first. The input event goes round again with the
// cleaned value, so a v-model ends up holding that rather than what was typed.
//
// Two ways in: the v-number-input directive for Vue, and data-number-input on server-rendered
// fields (bindAll, run from the admin entrypoint), with data-number-input-integer and
// data-number-input-negative as the options. A Vue field never carries the data attribute.
function clean(value) {
  return value.replace(/[^\d.-]/g, "");
}

// A lone "-" or "." passes: it is a figure half typed, and the handlers skip what isn't a number.
function problem(value, { negative = false, integer = false, min = "", max = "" } = {}) {
  if (!/^-?\d*(\.\d*)?$/.test(value)) return "Enter a number";
  if (!negative && value.startsWith("-")) return "Can't be negative";
  if (integer && value.includes(".")) return "Whole numbers only";

  const figure = Number(value);
  if (value === "" || Number.isNaN(figure)) return "";
  if (min !== "" && figure < Number(min)) return `Must be at least ${min}`;
  if (max !== "" && figure > Number(max)) return `Can't be more than ${max}`;
  return "";
}

// The bounds are read each time, so a Vue :max that changes is checked against its new value.
function check(el) {
  const message = problem(el.value, { ...el.numberInputOptions, min: el.min, max: el.max });
  el.setCustomValidity(message);
  el.classList.toggle("input-error", Boolean(message));
  if (message) {
    el.setAttribute("aria-invalid", "true");
  } else {
    el.removeAttribute("aria-invalid");
  }
}

const bound = new WeakSet();
const guardedForms = new WeakSet();

// simple_form renders its forms novalidate, so an invalid field would not stop the submit on its
// own - and Rails would quietly save "1.5" in an integer column as 1. Listened for in the capture
// phase, so it runs ahead of a Vue @submit.prevent handler bound to the same form.
function guard(form) {
  if (!form || guardedForms.has(form)) return;
  guardedForms.add(form);
  form.addEventListener("submit", (event) => {
    const fields = [...form.elements].filter((field) => bound.has(field));
    fields.forEach(check);
    const invalid = fields.find((field) => !field.checkValidity());
    if (!invalid) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    invalid.reportValidity();
  }, { capture: true });
}

export function bind(el, options) {
  el.numberInputOptions = options;
  if (bound.has(el)) return check(el);
  bound.add(el);

  el.addEventListener("input", () => {
    const cleaned = clean(el.value);
    if (cleaned === el.value) return check(el);

    const caret = Math.max(0, el.selectionStart - (el.value.length - cleaned.length));
    el.value = cleaned;
    el.setSelectionRange(caret, caret);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  });
  // Said as the field is left, not only when something tries to save it. Checked again first: a
  // @change handler that puts back a clamped value sets it without an input event.
  el.addEventListener("change", () => {
    check(el);
    if (!el.checkValidity()) el.reportValidity();
  });
  guard(el.form);
  check(el);
}

function optionsFromData(el) {
  return {
    integer: "numberInputInteger" in el.dataset,
    negative: "numberInputNegative" in el.dataset,
  };
}

function bindTree(root) {
  if (root.matches?.("[data-number-input]")) bind(root, optionsFromData(root));
  root.querySelectorAll?.("[data-number-input]").forEach((el) => {
    if (!bound.has(el)) bind(el, optionsFromData(el));
  });
}

// Binds the server-rendered figure fields on the page, and any added later (a purchase order's
// new line, a form opened in a modal).
export function bindAll(root = document) {
  bindTree(root);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) bindTree(node);
      });
    });
  }).observe(root === document ? document.body : root, { childList: true, subtree: true });
}

export default {
  mounted(el, binding) {
    bind(el, binding.value);
  },

  // Vue can put a value back without an input event: after a save, or when editing stops.
  updated(el, binding) {
    el.numberInputOptions = binding.value;
    check(el);
  },
};
